export interface MailboxAddressParts {
  name: string;
  email: string;
}

const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;
const UNSAFE_UNQUOTED_NAME_CHARACTER = /[()<>\[\]:;@\\,"]/;

/**
 * Split a compose recipient field without treating punctuation inside a
 * quoted display name or angle-bracket address as a separator.
 */
export function splitMailboxList(value: string): string[] {
  const result: string[] = [];
  let start = 0;
  for (const separator of mailboxSeparatorIndexes(value)) {
    const token = value.slice(start, separator).trim();
    if (token) result.push(token);
    start = separator + 1;
  }
  const token = value.slice(start).trim();
  if (token) result.push(token);
  return result;
}

/** Locate the compose recipient token containing the caret. */
export function mailboxTokenRange(
  value: string,
  caret: number,
): { start: number; end: number } {
  const safeCaret = Math.max(0, Math.min(caret, value.length));
  let start = 0;
  let end = value.length;
  for (const separator of mailboxSeparatorIndexes(value)) {
    if (separator < safeCaret) {
      start = separator + 1;
      continue;
    }
    end = separator;
    break;
  }
  return { start, end };
}

/**
 * Parse the deliberately small mailbox syntax accepted by Wintermute:
 * `person@example.com` or `Display Name <person@example.com>`. Quoted names
 * may contain commas and semicolons. RFC groups and comments are omitted on
 * purpose so ambiguous input cannot change the delivery envelope.
 */
export function parseMailboxAddress(value: string): MailboxAddressParts | null {
  const input = value.trim();
  if (!input || CONTROL_CHARACTER.test(input)) return null;

  const angles = angleBracketIndexes(input);
  if (!angles) return null;
  if (angles.left.length === 0 && angles.right.length === 0) {
    if (/[<>,;"\s]/.test(input)) return null;
    return { name: '', email: input };
  }
  if (angles.left.length !== 1 || angles.right.length !== 1) return null;

  const left = angles.left[0]!;
  const right = angles.right[0]!;
  if (right < left || input.slice(right + 1).trim()) return null;
  const email = input.slice(left + 1, right).trim();
  if (!email || /[<>,;"\s]/.test(email)) return null;

  const name = parseDisplayName(input.slice(0, left).trim());
  if (name === null || !name) return null;
  return { name, email };
}

export function mailboxEmail(value: string): string | null {
  return parseMailboxAddress(value)?.email ?? null;
}

/** Safely render a contact/provider name as mailbox syntax for the composer. */
export function formatMailboxAddress(name: string | null | undefined, email: string): string {
  const cleanEmail = email.trim();
  const cleanName = (name ?? '')
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleanName) return cleanEmail;
  const renderedName = UNSAFE_UNQUOTED_NAME_CHARACTER.test(cleanName)
    ? `"${cleanName.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}"`
    : cleanName;
  const formatted = `${renderedName} <${cleanEmail}>`;
  return formatted.length <= 512 ? formatted : cleanEmail;
}

function mailboxSeparatorIndexes(value: string): number[] {
  const result: number[] = [];
  let quoted = false;
  let escaped = false;
  let angleDepth = 0;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index]!;
    if (quoted) {
      if (escaped) {
        escaped = false;
      } else if (character === '\\') {
        escaped = true;
      } else if (character === '"') {
        quoted = false;
      }
      continue;
    }
    if (character === '"') {
      quoted = true;
    } else if (character === '<') {
      angleDepth += 1;
    } else if (character === '>' && angleDepth > 0) {
      angleDepth -= 1;
    } else if (angleDepth === 0 && isMailboxSeparator(character)) {
      result.push(index);
    }
  }
  return result;
}

function angleBracketIndexes(
  value: string,
): { left: number[]; right: number[] } | null {
  const left: number[] = [];
  const right: number[] = [];
  let quoted = false;
  let escaped = false;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index]!;
    if (quoted) {
      if (escaped) {
        escaped = false;
      } else if (character === '\\') {
        escaped = true;
      } else if (character === '"') {
        quoted = false;
      }
      continue;
    }
    if (character === '"') quoted = true;
    else if (character === '<') left.push(index);
    else if (character === '>') right.push(index);
  }
  return quoted || escaped ? null : { left, right };
}

function parseDisplayName(value: string): string | null {
  if (!value) return null;
  if (!value.startsWith('"')) {
    return UNSAFE_UNQUOTED_NAME_CHARACTER.test(value) ? null : value;
  }
  if (!value.endsWith('"') || value.length < 2) return null;
  let result = '';
  let escaped = false;
  for (let index = 1; index < value.length - 1; index += 1) {
    const character = value[index]!;
    if (escaped) {
      result += character;
      escaped = false;
    } else if (character === '\\') {
      escaped = true;
    } else if (character === '"') {
      return null;
    } else {
      result += character;
    }
  }
  return escaped ? null : result.trim();
}

function isMailboxSeparator(value: string): boolean {
  return value === ',' || value === ';' || value === '\n';
}
